/**
 * HollyTypingIndicator — shown while Holly is thinking.
 *
 * Port of the flox-website-5.0 indicator: instead of bare bouncing dots, it
 * rotates through short, logistics-flavoured status lines with a shimmer
 * sweep, so the wait feels alive and on-brand. The copy is CONTEXTUAL: the
 * last thing the visitor said biases the pool towards transport, warehouse,
 * or a general set. Keep the two copies in sync when changing wording.
 *
 * Layout matches the standard Holly message: FloxOLogo avatar on the left,
 * the shimmer line + a small dots accent inside the #F3F4F6 bubble with the
 * same sharp-bottom-left radius. Shimmer CSS lives in holly.css (NOT
 * global.css — the aiSidebarEmbed bundle doesn't load global.css).
 */

import { useEffect, useMemo, useState } from 'react';
import FloxOLogo from './FloxOLogo';

/* ── Copy pools (edit freely) ─────────────────────────────────────────────── */

const TRANSPORT_LINES = [
  'Plotting your route…',
  'Loading the pallets…',
  'Checking rate cards…',
  'Lining up carriers…',
  'Measuring the miles…',
  'Weighing up the load…',
];

const WAREHOUSE_LINES = [
  'Scanning nearby warehouses…',
  'Checking available space…',
  'Counting the racking…',
  'Rolling up the shutters…',
  'Measuring the floor space…',
];

const GENERAL_LINES = [
  'Warming up the forklift…',
  'Loading pallets…',
  'Checking the loading bay…',
  'Rallying the network…',
  'Consulting the logbook…',
];

const ROTATE_MS = 3500;

/* ── Flow detection from the visitor's last message ───────────────────────── */

const WAREHOUSE_RE =
  /(warehouse|storage|\bstore\b|\bspace\b|3pl|fulfil|racking|sq ?ft|square f|square m)/i;
const TRANSPORT_RE =
  /(transport|quote|deliver|ship|freight|haul|\bmove\b|collect|carrier|pallet|lorry|truck|\bto\b)/i;

function pickPool(lastUserMessage?: string): string[] {
  const text = (lastUserMessage ?? '').toLowerCase();
  if (WAREHOUSE_RE.test(text)) return WAREHOUSE_LINES;
  if (TRANSPORT_RE.test(text)) return TRANSPORT_LINES;
  return GENERAL_LINES;
}

/* Fisher–Yates on a copy — so repeated loads don't always open the same way. */
function shuffle(lines: string[]): string[] {
  const out = [...lines];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export default function HollyTypingIndicator({
  lastUserMessage,
}: {
  lastUserMessage?: string;
}) {
  // Freeze a shuffled pool for the lifetime of this indicator (one Holly turn).
  const lines = useMemo(
    () => shuffle(pickPool(lastUserMessage)),
    [lastUserMessage]
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % lines.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [lines.length]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 16,
      }}
      aria-label='Holly is thinking'
      aria-live='polite'
    >
      <FloxOLogo size={28} variant='colour' className='shrink-0' />
      <div
        style={{
          padding: '12px 16px',
          borderRadius: '12px 12px 12px 2px',
          backgroundColor: '#F3F4F6',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 20,
        }}
      >
        <span
          key={index}
          className='holly-thinking-text'
          style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          {lines[index]}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: '#94A3B8',
                display: 'inline-block',
                animation: 'holly-bounce 1.2s ease-in-out infinite',
                animationDelay: `${i * 150}ms`,
              }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}
